import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { Lock, Mail, AlertCircle, Loader2, CheckCircle2, Building2, MapPin } from "lucide-react";
import { ForgotPasswordModal, ForgotEmailModal } from "../components/AuthRecoveryModals";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function OperatorLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("signin");
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "", zip: "", state: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [awaiting, setAwaiting] = useState(false);
  const [showFP, setShowFP] = useState(false);
  const [showFE, setShowFE] = useState(false);

  useEffect(() => {
    // Bail out if a password recovery is in progress — never auto-open dashboard.
    try {
      if (sessionStorage.getItem("spotbounty_password_recovery_active") === "true") return;
    } catch { /* ignore */ }
    supabase.auth.getSession().then(async ({ data }) => {
      if (data?.session) await linkAndGo(data.session.access_token, null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function linkAndGo(token, profile) {
    try {
      const r = await fetch(`${API}/api/operator/auth/link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
        body: JSON.stringify(profile || {}),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        if (r.status === 400 && d.detail === "missing_profile") {
          setError(`Missing: ${(d.fields || []).join(", ")}`);
          setMode("signup"); setLoading(false); return;
        }
        setInfo("Check your email to confirm your account, then sign in.");
        setAwaiting(true); setLoading(false); return;
      }
    } catch { /* non-fatal */ }
    nav("/operator/dashboard", { replace: true });
  }

  async function submit(e) {
    e.preventDefault(); setLoading(true); setError(""); setInfo("");
    try {
      if (mode === "signup") {
        const missing = [];
        for (const k of ["first_name","last_name","phone","zip","state"]) if (!f[k]?.trim()) missing.push(k);
        if (missing.length) { setError(`Please provide: ${missing.join(", ")}`); setLoading(false); return; }
        const { data, error } = await supabase.auth.signUp({ email: f.email, password: f.password });
        if (error) { setError(error.message); setLoading(false); return; }
        const profile = {
          first_name: f.first_name.trim(), last_name: f.last_name.trim(),
          phone: f.phone.trim(), zip: f.zip.trim(), state: f.state.trim().toUpperCase(),
          company: f.company.trim(),
        };
        if (data?.session) return linkAndGo(data.session.access_token, profile);
        try { sessionStorage.setItem("sb_op_profile", JSON.stringify(profile)); } catch { /* ignore */ }
        setInfo("Signup received. Check your email to confirm.");
        setAwaiting(true); setMode("signin"); setLoading(false);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email: f.email, password: f.password });
        if (error) { setError(error.message); setLoading(false); return; }
        let profile = null;
        try {
          const raw = sessionStorage.getItem("sb_op_profile");
          if (raw) { profile = JSON.parse(raw); sessionStorage.removeItem("sb_op_profile"); }
        } catch { /* ignore */ }
        return linkAndGo(data.session.access_token, profile);
      }
    } catch { setError("Unexpected error"); setLoading(false); }
  }

  async function resendVerify() {
    if (!f.email) { setError("Enter your email above first"); return; }
    const { error } = await supabase.auth.resend({ type: "signup", email: f.email });
    if (error) setError(error.message);
    else setInfo("Verification email re-sent.");
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex flex-col">
      <Seo title="Operator Login" description="SpotBounty Operator portal" path="/operator/login" noindex />
      <header className="border-b border-[#2A2F34]"><div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/"><SBLogo /></Link>
        <div className="text-[11px] text-[#A1A1AA]">Operator Portal</div>
      </div></header>
      <main className="flex-1 grid md:grid-cols-2 items-stretch">
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <div className="max-w-md w-full mx-auto">
            <div className="sb-eyebrow">Operator Portal</div>
            <h1 className="mt-2 text-[36px] leading-tight font-black">
              {mode === "signup" ? "Apply to Buy Locations." : "Welcome Back."}
            </h1>
            <p className="mt-3 text-[13.5px] text-[#A1A1AA]">
              Browse pre-qualified vending opportunities. All purchases require SpotBounty approval.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="operator-login-form">
              {mode === "signup" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <TInput label="First name" v={f.first_name} onChange={(x) => setF({ ...f, first_name: x })} testid="op-first-name" />
                    <TInput label="Last name" v={f.last_name} onChange={(x) => setF({ ...f, last_name: x })} testid="op-last-name" />
                  </div>
                  <TInput label="Company (optional)" v={f.company} onChange={(x) => setF({ ...f, company: x })} testid="op-company" />
                  <TInput label="Phone" v={f.phone} onChange={(x) => setF({ ...f, phone: x })} testid="op-phone" />
                  <div className="grid grid-cols-2 gap-2">
                    <TInput label="ZIP" v={f.zip} onChange={(x) => setF({ ...f, zip: x })} testid="op-zip" />
                    <TInput label="State" v={f.state} onChange={(x) => setF({ ...f, state: x.toUpperCase() })} testid="op-state" />
                  </div>
                </>
              )}
              <label className="block">
                <div className="sb-label">Email</div>
                <div className="relative"><Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input type="email" required className="sb-input pl-9" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="op-email" /></div>
              </label>
              <label className="block">
                <div className="sb-label">Password</div>
                <div className="relative"><Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input type="password" required className="sb-input pl-9" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="op-password" /></div>
              </label>
              {error && <div className="text-[12px] text-[#EF4444] flex items-center gap-1"><AlertCircle size={12} /> {error}</div>}
              {info && <div className="text-[12px] text-[#22C55E] flex items-center gap-1"><CheckCircle2 size={12} /> {info}</div>}
              <button type="submit" disabled={loading} className="sb-btn-primary w-full" data-testid="op-submit">
                {loading ? <Loader2 size={14} className="animate-spin" /> : (mode === "signup" ? "Create Account" : "Sign In")}
              </button>
              {awaiting && (
                <button type="button" onClick={resendVerify} className="w-full text-[12px] text-[#FFC107] hover:underline">Resend verification email</button>
              )}
              {mode === "signin" && (
                <div className="flex items-center justify-between text-[12px] pt-1">
                  <button type="button" onClick={() => setShowFP(true)} className="text-[#FFC107] hover:underline" data-testid="op-forgot-password">Forgot password?</button>
                  <button type="button" onClick={() => setShowFE(true)} className="text-[#A1A1AA] hover:text-white" data-testid="op-forgot-email">Forgot email?</button>
                </div>
              )}
            </form>
            <div className="mt-6 text-[12px] text-[#A1A1AA]">
              {mode === "signup" ? (
                <>Already have an account? <button onClick={() => { setMode("signin"); setError(""); }} className="text-[#FFC107] hover:underline">Sign in</button></>
              ) : (
                <>New operator? <button onClick={() => { setMode("signup"); setError(""); }} className="text-[#FFC107] hover:underline">Apply for access</button></>
              )}
            </div>
          </div>
        </div>
        <div className="hidden md:flex bg-[#0B0E11] border-l border-[#2A2F34] p-12 items-center">
          <div className="max-w-md">
            <div className="sb-eyebrow text-[#FFC107]">The SpotBounty Advantage</div>
            <h2 className="mt-3 text-[26px] font-black leading-tight">Skip the cold outreach. Buy locations with paperwork already done.</h2>
            <ul className="mt-6 space-y-3 text-[13px] text-[#A1A1AA]">
              <li className="flex items-start gap-2"><MapPin size={14} className="text-[#FFC107] mt-0.5" /> Vetted opportunities with signed agreements.</li>
              <li className="flex items-start gap-2"><Building2 size={14} className="text-[#FFC107] mt-0.5" /> Business, decision-maker, placement details unlocked on purchase.</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#FFC107] mt-0.5" /> One-time fee. No subscriptions, no commissions to SpotBounty on your revenue.</li>
            </ul>
          </div>
        </div>
      </main>
      <ForgotPasswordModal open={showFP} onClose={() => setShowFP(false)} role="operator" />
      <ForgotEmailModal open={showFE} onClose={() => setShowFE(false)} defaultRole="operator" />
    </div>
  );
}

function TInput({ label, v, onChange, testid }) {
  return (
    <label className="block">
      <div className="sb-label">{label}</div>
      <input className="sb-input" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}
